import { useContext } from 'react';
import { buildKnowledgeNodePath, useKnowledgeNode } from '@/entities/knowledge';
import { buildKmEntityPath, getKmEntityText, getKmEntityTitle, useKmEntity } from '@/entities/ontology';
import type { PermissionCode } from '@/entities/profile';
import { ExpertReportModelModeContext } from './expertReportModelModeContext';

/** Знание, названное в комментарии результата, вместе с местом, где оно живёт. */
export type ReportKnowledgeNode = {
  /** Наименование знания; пусто, пока знание читается или если его не нашлось */
  name: string;
  /** Текст знания */
  text: string;
  isLoading: boolean;
  /** Адрес знания в разделе, которому оно принадлежит */
  path: string;
  /** Право на раздел, куда ведёт переход к знанию */
  permission: PermissionCode;
};

/**
 * Знание по ссылке из комментария результата заключения: у заключения на модели знаний —
 * нода модели, у прежнего — нода прежней базы знаний. Номера этих нод независимы и совпадают
 * сплошь и рядом, поэтому знание читается ровно в той модели, по которой посчитано заключение.
 *
 * Само знание читается любым, кто видит результат; право нужно лишь на переход в раздел.
 */
export const useReportKnowledgeNode = (nodeId?: number): ReportKnowledgeNode => {
  const modelMode = useContext(ExpertReportModelModeContext);
  const isKmMode = modelMode === 'km';

  const legacyNode = useKnowledgeNode(isKmMode ? undefined : nodeId);
  const kmEntity = useKmEntity(isKmMode ? nodeId : undefined);

  if (isKmMode) {
    return {
      name: kmEntity.data ? getKmEntityTitle(kmEntity.data) : '',
      text: kmEntity.data ? getKmEntityText(kmEntity.data) : '',
      isLoading: kmEntity.isLoading,
      path: buildKmEntityPath(nodeId),
      permission: 'VIEW_KM_ENTITY_TYPE',
    };
  }

  return {
    name: legacyNode.data?.name ?? '',
    text: legacyNode.data?.text ?? '',
    isLoading: legacyNode.isLoading,
    path: buildKnowledgeNodePath(nodeId),
    permission: 'VIEW_KNOWLEDGE',
  };
};
